import React, { useState } from "react";
import clsx from "clsx";
import styles from "./styles";

import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Avatar from "@material-ui/core/Avatar";
import defaultUserImg from "asset/img/default-avatar.jpg";

import {
  Call as CallIcon,
  StarRate as StarRateIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  ExitToApp as ExitToAppIcon,
  AddCircle as AddCircleIcon,
  Language as LanguageIcon,
  Dashboard as LeaderBoardIcon,
} from "@material-ui/icons";

import IosMapOutline from "react-ionicons/lib/IosMapOutline";
import MdThumbsUp from "react-ionicons/lib/MdThumbsUp";
import IosTime from "react-ionicons/lib/IosTime";
import IosKeypad from "react-ionicons/lib/IosKeypad";
import IosCreate from "react-ionicons/lib/IosCreate";
import MdClipboard from "react-ionicons/lib/MdClipboard";
import MdContact from "react-ionicons/lib/MdContact";
import MdPersonAdd from "react-ionicons/lib/MdPersonAdd";
import MdCheckmarkCircle from "react-ionicons/lib/MdCheckmarkCircle";
import MdUnlock from "react-ionicons/lib/MdUnlock";
import IosHappy from "react-ionicons/lib/IosHappy";
import MdBriefcase from "react-ionicons/lib/MdBriefcase";
import IosPhonePortrait from "react-ionicons/lib/IosPhonePortrait";


import { useSelector, useDispatch } from "react-redux";
import { reducerType } from "redux/reducers";
import { GET_SERVICES_REDUCER } from "redux/reducers/service/actionTypes";
import { LOGOUT_REQUEST } from "redux/reducers/auth/actionTypes";
import AntSwitch from "components/SwitchButton";
import { useTranslation } from "react-i18next";
import { UserRole } from "common";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDonate } from "@fortawesome/free-solid-svg-icons";
import { faHandHoldingWater as VolunteerValidateIcon } from "@fortawesome/free-solid-svg-icons";
import { faHandHoldingMedical as ManageVolunteerServicesIcon } from "@fortawesome/free-solid-svg-icons";
import { faHandHoldingHeart as ManageVolunteersIcon } from "@fortawesome/free-solid-svg-icons";
import { faSuitcase as ManageVolunteersWorkIcon } from "@fortawesome/free-solid-svg-icons";

import { Icon } from "@iconify/react";
import charityIcon from "@iconify-icons/mdi/charity";
import { removeDeviceToken } from "utils/firebase-message";

interface SideBarProps {
  menuIsOpen: boolean;
  clickOpenMenu: Function;
  openUrl: Function;
  isMapView: boolean;
  setIsMapView: Function;
  setOpenServices: Function;
  isOpenService: boolean;
  setCategory: Function;
  category: string;
  badges: any;
}

const SideBar = React.memo((props: SideBarProps) => {
  const translate = useTranslation().t;
  const current_user = useSelector(
    (state: reducerType) => state.auth.current_user
  );

  // Updated role logic to handle volunteer roles
  const role = (() => {
    if (!current_user || !current_user.isLogin) {
      return "GUEST";
    }

    if (current_user.isAdmin) {
      return UserRole.Administrator;
    }
    // Check for volunteer roles
    if (current_user.roles && current_user.roles.length > 0) {
      if (current_user.roles.includes(UserRole.VolunteerSenior)) {
        return UserRole.VolunteerSenior;
      }
      if (current_user.roles.includes(UserRole.VolunteerLead)) {
        return UserRole.VolunteerLead;
      }
      if (current_user.roles.includes(UserRole.Volunteer)) {
        return UserRole.Volunteer;
      }
    }

    return "USER";
  })();

  const VOLUNTEER_GROUP = [
    UserRole.Volunteer,
    UserRole.VolunteerLead,
    UserRole.VolunteerSenior,
  ];

  const dispatch = useDispatch();
  const classes = styles();
  const {
    clickOpenMenu,
    menuIsOpen,
    isMapView,
    setIsMapView,
    setOpenServices,
    isOpenService,
    category,
    setCategory,
    badges,
  } = props;

  const [topKudos, setTopKudos] = useState(
    Boolean(JSON.parse(sessionStorage.getItem("@shelter_isTopKudos")))
  );

  const setSortTopKudos = (open) => {
    sessionStorage.setItem("@shelter_isTopKudos", JSON.stringify(open));
    window.scrollTo(0, 0);
    let params = {
      skip: 0,
      sort: open ? "likes" : "",
    };
    dispatch({
      type: GET_SERVICES_REDUCER,
      params: params,
    });
    setTopKudos(open);
  };

  const openUrl = (url: string) => {
    props.openUrl(url);
  };

  const openNewPage = (url) => {
    var win = window.open(url, "_blank");
    win.focus();
  };

  const toggleDrawer = (open: boolean) => (
    event: React.KeyboardEvent | React.MouseEvent
  ) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    clickOpenMenu(open);
  };

  const logout = async () => {
    await Promise.all([
      removeDeviceToken(),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]);
    dispatch({
      type: LOGOUT_REQUEST,
    });
  };

  const [language, setLanguage] = useState(
    JSON.parse(localStorage.getItem("@shelter_language"))
      ? JSON.parse(localStorage.getItem("@shelter_language"))
      : "en"
  );

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    localStorage.setItem(
      "@shelter_language",
      JSON.stringify(event.target.value as string)
    );
    setLanguage(event.target.value as string);
    window.location.reload();
  };

  const getAvatarUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    const baseUrl = process.env.REACT_APP_DOMAIN_API || "";
    return `${baseUrl}${imageUrl}`;
  };

  const _buildItem = ({
    onClick,
    icon,
    text,
    badgeContent,
  }: {
    onClick: () => void;
    icon: React.ReactNode;
    text: string;
    badgeContent?: number;
  }) => (
    <>
      <Divider />
      <ListItem onClick={onClick} className={classes.optMenu} button>
        <ListItemIcon>{icon}</ListItemIcon>
        {badgeContent !== undefined ? (
          <Badge
            max={9999999}
            classes={{ badge: classes.badge }}
            badgeContent={badgeContent}
            color="error"
          >
            <ListItemText primary={translate(text)} />
          </Badge>
        ) : (
          <ListItemText primary={translate(text)} />
        )}
      </ListItem>
    </>
  );

  const isLoggedIn = role !== "GUEST";
  const avatarUrl = getAvatarUrl(current_user?.imageUrl);
  const displayName = current_user?.displayName;

  const list = () => (
    <div
      className={clsx(classes.list)}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      {/* Case 1: All users (include guest) can see these options */}
      <div
        className={clsx(classes.toolbar, {
          [classes.toolbarLoggedIn]: isLoggedIn,
        })}
      >
        {isLoggedIn ? (
          <div className={classes.userRow}>
            <Avatar
              src={avatarUrl || defaultUserImg}
              alt={displayName || current_user.email || "User"}
              className={classes.avatar}
            >
              {(displayName || current_user.email || "U").charAt(0).toUpperCase()}
            </Avatar>
            <div className={classes.userMeta}>
              {!!displayName && (
                <Typography className={classes.displayName} noWrap>
                  {displayName}
                </Typography>
              )}
              {!!current_user.email && (
                <Typography className={classes.emailText}>
                  {current_user.email}
                </Typography>
              )}
            </div>
          </div>
        ) : (
          <p>{translate("OPTIONS")}</p>
        )}
      </div>
      <Divider />
      <ListItem className={classes.optMenu} button>
        <ListItemIcon>
          <IosTime color="#6A46E5" fontSize="18px" />
        </ListItemIcon>
        <ListItemText primary={translate("OPEN_SERVICES")} />
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <AntSwitch
                checked={isOpenService}
                onChange={() => {
                  setOpenServices(!isOpenService);
                }}
                name="checkedC"
              />
            </Grid>
          </Grid>
        </Typography>
      </ListItem>
      <Divider />
      <ListItem className={classes.listSelections}>
        {["MEN", "WOMEN", "YOUTH_AND_KIDS", "ALL"].map((text, index) => {
          return (
            <span
              className={clsx(
                {
                  [classes.borderRadiusLeft]: index === 0,
                  [classes.borderRadiusRight]: index === 3,
                  [classes.selectActive]: category === text,
                },
                classes.selection
              )}
              onClick={() => setCategory(text)}
              key={text}
            >
              {translate(text)}
            </span>
          );
        })}
      </ListItem>
      <ListItem className={classes.listSelections}>
        {["SENIORS", "DISABLED", "FAMILIES", "LGBT"].map((text, index) => {
          return (
            <span
              className={clsx(
                {
                  [classes.borderRadiusLeft]: index === 0,
                  [classes.borderRadiusRight]: index === 3,
                  [classes.selectActive]: category === text,
                },
                classes.selection
              )}
              onClick={() => setCategory(text)}
              key={text}
            >
              {translate(text)}
            </span>
          );
        })}
      </ListItem>
      <Divider />
      <ListItem className={classes.optMenu} button>
        <ListItemIcon>
          <IosMapOutline color="#6A46E5" fontSize="18px" />
        </ListItemIcon>
        <ListItemText primary={translate("MAP_VIEW")} />
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <AntSwitch
                checked={isMapView}
                onChange={() => {
                  setIsMapView(!isMapView);
                }}
                name="checkedC"
              />
            </Grid>
          </Grid>
        </Typography>
      </ListItem>
      <Divider />
      {role === UserRole.Administrator && (
        <>
          <ListItem className={classes.optMenu} button>
            <ListItemIcon>
              <MdThumbsUp color="#6A46E5" fontSize="18px" />
            </ListItemIcon>
            <ListItemText primary={translate("TOP_KUDOS")} />
            <Typography component="div">
              <Grid component="label" container alignItems="center" spacing={1}>
                <Grid item>
                  <AntSwitch
                    checked={topKudos}
                    onChange={() => {
                      setSortTopKudos(!topKudos);
                    }}
                    name="checkedC"
                  />
                </Grid>
              </Grid>
            </Typography>
          </ListItem>
          <Divider />
        </>
      )}
      <ListItem
        onClick={() => openUrl("/crisis_lines")}
        className={classes.optMenu}
        button
      >
        <ListItemIcon>
          <CallIcon className={classes.iconM} />
        </ListItemIcon>
        <ListItemText primary={translate("CRISIS_LINES")} />
      </ListItem>
      <Divider />
      <ListItem className={classes.optMenu} button>
        <ListItemIcon>
          <LanguageIcon className={classes.iconM} />
        </ListItemIcon>
        <ListItemText primary={translate("LANGUAGE_LABEL")} />
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Select value={language} onChange={handleChange} disableUnderline>
                <MenuItem value={"en"}>English</MenuItem>
                <MenuItem value={"es"}>Español</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Typography>
      </ListItem>

      {_buildItem({
        onClick: () => openUrl("/my_favorites"),
        icon: <StarRateIcon className={classes.iconL} />,
        text: "MY_FAVORITES",
      })}

      {/* Case 2: Logged In User*/}
      {role !== "GUEST" && (
        <>
          {/* Case 2.1: Approved Volunteer,Approved Volunteer Lead,Approved Volunteer Senior, User, Administrator */}
          {
            ([UserRole.Administrator, UserRole.User, UserRole.SuperUser].includes(role) ||
              (VOLUNTEER_GROUP.includes(role) && current_user.isApproved))
            //
            && (
              <>
                {[UserRole.Administrator, ...VOLUNTEER_GROUP].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/validate_list_services"),
                        icon: (
                          <FontAwesomeIcon
                            icon={VolunteerValidateIcon}
                            className={classes.iconS}
                          />
                        ),
                        text: "VALIDATE_SERVICES",
                      })}
                    </>
                  )}
                {[UserRole.Administrator, UserRole.VolunteerLead].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/manage_volunteer_services"),
                        icon: (
                          <FontAwesomeIcon
                            icon={ManageVolunteerServicesIcon}
                            className={classes.iconS}
                          />
                        ),
                        text: "MANAGE_VALIDATE_SERVICES",
                        badgeContent: badges ? Number(badges.countVolunteerServices) : 0,
                      })}
                    </>
                  )}
                {[UserRole.Administrator, UserRole.VolunteerLead].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/manage_volunteers"),
                        icon: (
                          <FontAwesomeIcon
                            icon={ManageVolunteersIcon}
                            className={classes.iconS}
                          />
                        ),
                        text: "MANAGE_VOLUNTEERS",
                        badgeContent: badges ? Number(badges.countVolunteers) : 0,
                      })}
                    </>
                  )}
                {[UserRole.Administrator, UserRole.VolunteerLead].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/manage_volunteers_work"),
                        icon: (
                          <FontAwesomeIcon
                            icon={ManageVolunteersWorkIcon}
                            className={classes.iconS}
                          />
                        ),
                        text: "MANAGE_VOLUNTEERS_WORK",
                      })}
                    </>
                  )}
                {[UserRole.Administrator, ...VOLUNTEER_GROUP].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/my-work"),
                        icon: <MdBriefcase color="#6A46E5" fontSize="18px" />,
                        text: "MY_WORK",
                      })}
                    </>
                  )}
                {_buildItem({
                  onClick: () => openUrl("/leaderboard"),
                  icon: <LeaderBoardIcon className={classes.iconM} />,
                  text: "VOLUNTEER_LEADERBOARD",
                })}
                {role !== UserRole.Volunteer &&
                  _buildItem({
                    onClick: () => openUrl("/add_service"),
                    icon: <AddCircleIcon className={classes.iconM} />,
                    text: "ADD_SERVICE",
                  })}

                {[UserRole.Administrator].includes(role) &&
                  _buildItem({
                    onClick: () => openUrl("/crisis_lines/new"),
                    icon: <AddCircleIcon className={classes.iconM} />,
                    text: "ADD_CRISIS_LINE",
                  })}

                {[
                  UserRole.Administrator,
                  UserRole.User,
                  UserRole.VolunteerLead,
                  UserRole.VolunteerSenior,
                ].includes(role) &&
                  _buildItem({
                    onClick: () => openUrl("/manage_services"),
                    icon: <IosKeypad color="#6A46E5" fontSize="18px" />,
                    text: "MANAGE_SERVICES",
                    badgeContent: badges
                      ? Number(badges.countManagedServices)
                      : 0,
                  })}

                {[UserRole.Administrator, UserRole.VolunteerLead].includes(
                  role
                ) &&
                  _buildItem({
                    onClick: () => openUrl("/feedbacks"),
                    icon: <IosHappy color="#6A46E5" fontSize="18px" />,
                    text: "MANAGE_FEEDBACK",
                    badgeContent: badges
                      ? Number(badges.countUnReadFeedbacks)
                      : 0,
                  })}

                {[UserRole.Administrator, UserRole.User].includes(role) &&
                  _buildItem({
                    onClick: () => openUrl("/available_beds"),
                    icon: <MdCheckmarkCircle color="#6A46E5" fontSize="18px" />,
                    text: "UPDATE_SHELTER",
                    badgeContent: badges
                      ? Number(badges.countManagedShelters)
                      : 0,
                  })}

                {[UserRole.Administrator, UserRole.VolunteerLead].includes(
                  role
                ) && (
                    <>
                      {_buildItem({
                        onClick: () => openUrl("/manage_approvals"),
                        icon: <MdUnlock color="#6A46E5" fontSize="18px" />,
                        text: "MANAGE_APPROVALS",
                        badgeContent: badges
                          ? Number(badges.countNotApprovedServices)
                          : 0,
                      })}
                    </>
                  )}

                {[UserRole.Administrator].includes(role) &&
                  _buildItem({
                    onClick: () => openUrl("/manage_users"),
                    icon: <MdPersonAdd color="#6A46E5" fontSize="18px" />,
                    text: "MANAGE_USERS",
                    badgeContent: badges ? Number(badges.countTotalUsers) : 0,
                  })}

                {[UserRole.Administrator].includes(role) &&
                  _buildItem({
                    onClick: () => openUrl("/manage_app_update"),
                    icon: <IosPhonePortrait color="#6A46E5" fontSize="20px" />,
                    text: "MANAGE_APP_UPDATE",
                  })}
              </>
            )}

          {/* Case 2.2: All logged users */}
          {_buildItem({
            onClick: () => openUrl("/update_profile"),
            icon: <MdContact color="#6A46E5" fontSize="18px" />,
            text: "MANAGE_PROFILE",
          })}

          {/* {current_user.lastMethod === "LOCAL" &&
            _buildItem({
              onClick: () => openUrl("/change_password"),
              icon: <FingerprintIcon className={classes.iconM} />,
              text: "CHANGE_PASSWORD",
            })} */}
        </>
      )}

      {/* Case 3: Guest, User*/}
      {["GUEST", "USER"].includes(role) && (
        <>
          {_buildItem({
            onClick: () => openUrl("/about"),
            icon: <InfoIcon className={classes.iconM} />,
            text: "ABOUT_SHELTER",
          })}
          {_buildItem({
            onClick: () => openUrl("/introduce"),
            icon: <HelpIcon className={classes.iconM} />,
            text: "SHOW_TUTORIAL",
          })}
          {_buildItem({
            onClick: () => openUrl("/feedback"),
            icon: <IosCreate color="#6A46E5" fontSize="18px" />,
            text: "GIVE_FEEDBACK",
          })}
          {_buildItem({
            onClick: () => openUrl("/login?volunteer=true"),
            icon: <Icon icon={charityIcon} className={classes.iconM} />,
            text: "VOLUNTEER",
          })}
          {_buildItem({
            onClick: () => openUrl("/leaderboard"),
            icon: <LeaderBoardIcon className={classes.iconM} />,
            text: "VOLUNTEER_LEADERBOARD",
          })}
          {_buildItem({
            onClick: () =>
              openNewPage("https://www.paypal.com/fundraiser/charity/3945196"),
            icon: <FontAwesomeIcon icon={faDonate} className={classes.iconM} />,
            text: "DONATE",
          })}
          {_buildItem({
            onClick: () => openUrl("/terms_of_use"),
            icon: <MdClipboard color="#6A46E5" fontSize="18px" />,
            text: "TERMS_OF_USE",
          })}
          {_buildItem({
            onClick: () => openUrl("/privacy_policy"),
            icon: <SecurityIcon className={classes.iconM} />,
            text: "PRIVACY_POLICY",
          })}
        </>
      )}

      {/* Case 4: Logout, Login*/}
      {role !== "GUEST"
        ? _buildItem({
          onClick: logout,
          icon: <ExitToAppIcon className={classes.iconM} />,
          text: "LOGOUT",
        })
        : _buildItem({
          onClick: () => openUrl("/login"),
          icon: <ExitToAppIcon className={classes.iconM} />,
          text: "LOGIN_AND_SIGNUP",
        })}
    </div>
  );

  return (
    <React.Fragment key={"left"}>
      <Drawer anchor={"left"} open={menuIsOpen} onClose={toggleDrawer(false)}>
        {list()}
      </Drawer>
    </React.Fragment>
  );
});

export default SideBar;
